import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useAuth } from '@/hooks/useAuth';

interface AdminSidebarProps {
  getUserInitials: (name: string) => string;
  isOpen?: boolean;
  onClose?: () => void;
}

export function AdminSidebar({ getUserInitials, isOpen = false, onClose }: AdminSidebarProps) {
  const location = useLocation();
  const { user } = useAuthStore();
  const { logout } = useAuth();
  const isSuperRoot = (user?.roles ?? []).includes('ROLE_SUPER_ROOT');

  // Extract project ID from URL if present
  const projectIdMatch = location.pathname.match(/\/admin\/projects\/(\d+)/);
  const currentProjectId = projectIdMatch ? projectIdMatch[1] : null;

  // State for Analyses menu expansion
  const [isAnalysesOpen, setIsAnalysesOpen] = useState(() => {
    // Open by default if we're on an analyses page
    return location.pathname.includes('/analyses');
  });

  const isActive = (path: string) => {
    if (path === '/admin/dashboard') {
      // Dashboard is active only if we're exactly on /admin/dashboard
      return location.pathname === '/admin/dashboard';
    }
    if (path === '/admin/users') {
      return location.pathname === '/admin/users';
    }
    if (path === '/admin/configuration') {
      return location.pathname.startsWith('/admin/configuration');
    }
    
    // Check exact match first
    if (location.pathname === path) {
      return true;
    }
    
    // For attribute-data, only active if we're exactly on that path (not on a sub-path like parties-communes)
    // This prevents "Attribute data" from being active when on "Partie commune"
    if (path.includes('/configuration/attribute-data') && !path.includes('/parties-communes')) {
      // If we're on a sub-path of attribute-data (like parties-communes), don't mark attribute-data as active
      if (location.pathname.startsWith(path + '/')) {
        return false;
      }
      // Otherwise, check if we're exactly on attribute-data
      return location.pathname === path;
    }
    
    // For other paths, use startsWith for parent paths
    return location.pathname.startsWith(path);
  };

  // Close sidebar when route changes on mobile
  useEffect(() => {
    if (isOpen && onClose) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Auto-open Analyses menu when navigating to analyses pages
  useEffect(() => {
    if (location.pathname.includes('/analyses')) {
      setIsAnalysesOpen(true);
    }
  }, [location.pathname]);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
        />
      )}
      {/* Sidebar */}
      <aside
        className={`fixed md:relative inset-y-0 left-0 z-50 w-64 flex-shrink-0 flex-col bg-white dark:bg-[#16202A] border-r border-slate-200 dark:border-[#1E293B] transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0 md:flex`}
      >
      <div className="h-16 flex items-center justify-between px-6 border-b border-slate-200 dark:border-[#1E293B]">
        <div className="flex items-center gap-2 text-primary font-bold text-xl">
          <span className="material-icons-outlined">domain</span>
          <span>GTB Admin</span>
        </div>
        {/* Close button for mobile */}
        {onClose && (
          <button
            onClick={onClose}
            className="md:hidden p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white"
          >
            <span className="material-icons-outlined">close</span>
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto py-4">
        <nav className="px-3 space-y-1">
          <Link
            to="/admin/dashboard"
            className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              isActive('/admin/dashboard')
                ? 'bg-primary/10 text-primary dark:text-accent'
                : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
            }`}
          >
            <span className="material-icons-outlined text-lg">dashboard</span>
            Dashboard
          </Link>
          <div className="pt-4 pb-2 px-3 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
            Gestion
          </div>
          <Link
            to="/admin/projects"
            className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              (location.pathname === '/admin/projects' || location.pathname.includes('/projects/')) && !currentProjectId
                ? 'bg-primary/10 text-primary dark:text-accent'
                : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
            }`}
          >
            <span className="material-icons-outlined text-lg">folder_open</span>
            Projets
          </Link>

          {currentProjectId && (
            <div className="mt-2 ml-3 pl-3 border-l-2 border-slate-200 dark:border-slate-700 space-y-1">
              <Link
                to={`/admin/projects/${currentProjectId}/areas`}
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(`/admin/projects/${currentProjectId}/areas`)
                    ? 'bg-primary/10 text-primary dark:text-accent'
                    : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                }`}
              >
                <span className="material-icons-outlined text-lg">account_tree</span>
                Zones
              </Link>
              <Link
                to={`/admin/projects/${currentProjectId}/association`}
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(`/admin/projects/${currentProjectId}/association`)
                    ? 'bg-primary/10 text-primary dark:text-accent'
                    : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                }`}
              >
                <span className="material-icons-outlined text-lg">drag_indicator</span>
                Associations
              </Link>
              <Link
                to={`/admin/projects/${currentProjectId}/equipments`}
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(`/admin/projects/${currentProjectId}/equipments`)
                    ? 'bg-primary/10 text-primary dark:text-accent'
                    : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                }`}
              >
                <span className="material-icons-outlined text-lg">router</span>
                Equipements
              </Link>
              <Link
                to={`/admin/projects/${currentProjectId}/alerts`}
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(`/admin/projects/${currentProjectId}/alerts`)
                    ? 'bg-primary/10 text-primary dark:text-accent'
                    : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                }`}
              >
                <span className="material-icons-outlined text-lg">notifications</span>
                Alertes
              </Link>
              <Link
                to={`/admin/projects/${currentProjectId}/members`}
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(`/admin/projects/${currentProjectId}/members`)
                    ? 'bg-primary/10 text-primary dark:text-accent'
                    : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                }`}
              >
                <span className="material-icons-outlined text-lg">group</span>
                Membres
              </Link>
              <Link
                to={`/admin/projects/${currentProjectId}/box-accounts`}
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(`/admin/projects/${currentProjectId}/box-accounts`)
                    ? 'bg-primary/10 text-primary dark:text-accent'
                    : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                }`}
              >
                <span className="material-icons-outlined text-lg">cloud</span>
                Comptes IoT
              </Link>
              <Link
                to={`/admin/projects/${currentProjectId}/image`}
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(`/admin/projects/${currentProjectId}/image`)
                    ? 'bg-primary/10 text-primary dark:text-accent'
                    : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                }`}
              >
                <span className="material-icons-outlined text-lg">image</span>
                Image
              </Link>
              <div>
                <div
                  className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive(`/admin/projects/${currentProjectId}/analyses`)
                      ? 'bg-primary/10 text-primary dark:text-accent'
                      : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Link
                    to={`/admin/projects/${currentProjectId}/analyses`}
                    className="flex items-center gap-3 flex-1"
                  >
                    <span className="material-icons-outlined text-lg">analytics</span>
                    <span>Analyses</span>
                  </Link>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setIsAnalysesOpen(!isAnalysesOpen);
                    }}
                    className="p-1 hover:bg-slate-200 dark:hover:bg-gray-700 rounded transition-colors"
                    aria-label="Toggle analyses menu"
                  >
                    <span
                      className={`material-icons-outlined text-lg transition-transform duration-200 ${
                        isAnalysesOpen ? 'rotate-180' : ''
                      }`}
                    >
                      expand_more
                    </span>
                  </button>
                </div>
                {isAnalysesOpen && (
                  <div className="ml-6 mt-1 space-y-1">
                <Link
                  to={`/admin/projects/${currentProjectId}/analyses/consommation`}
                  className={`flex items-center gap-3 px-3 py-2 text-[13px] font-medium rounded-md transition-colors ${
                    isActive(`/admin/projects/${currentProjectId}/analyses/consommation`)
                      ? 'bg-primary/10 text-primary dark:text-accent'
                      : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <span className="material-icons-outlined text-base">bolt</span>
                  Consommation
                </Link>
                <Link
                  to={`/admin/projects/${currentProjectId}/analyses/puissance`}
                  className={`flex items-center gap-3 px-3 py-2 text-[13px] font-medium rounded-md transition-colors ${
                    isActive(`/admin/projects/${currentProjectId}/analyses/puissance`)
                      ? 'bg-primary/10 text-primary dark:text-accent'
                      : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <span className="material-icons-outlined text-base">electrical_services</span>
                  Puissance
                </Link>
                <Link
                  to={`/admin/projects/${currentProjectId}/analyses/confort`}
                  className={`flex items-center gap-3 px-3 py-2 text-[13px] font-medium rounded-md transition-colors ${
                    isActive(`/admin/projects/${currentProjectId}/analyses/confort`)
                      ? 'bg-primary/10 text-primary dark:text-accent'
                      : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <span className="material-icons-outlined text-base">thermostat</span>
                  Confort
                </Link>
                <Link
                  to={`/admin/projects/${currentProjectId}/analyses/comparaisons`}
                  className={`flex items-center gap-3 px-3 py-2 text-[13px] font-medium rounded-md transition-colors ${
                    isActive(`/admin/projects/${currentProjectId}/analyses/comparaisons`)
                      ? 'bg-primary/10 text-primary dark:text-accent'
                      : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <span className="material-icons-outlined text-base">compare_arrows</span>
                  Comparaisons
                </Link>
                <Link
                  to={`/admin/projects/${currentProjectId}/analyses/rapports`}
                  className={`flex items-center gap-3 px-3 py-2 text-[13px] font-medium rounded-md transition-colors ${
                    isActive(`/admin/projects/${currentProjectId}/analyses/rapports`)
                      ? 'bg-primary/10 text-primary dark:text-accent'
                      : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <span className="material-icons-outlined text-base">description</span>
                  Rapports
                </Link>
                <Link
                  to={`/admin/projects/${currentProjectId}/analyses/logement`}
                  className={`flex items-center gap-3 px-3 py-2 text-[13px] font-medium rounded-md transition-colors ${
                    isActive(`/admin/projects/${currentProjectId}/analyses/logement`)
                      ? 'bg-primary/10 text-primary dark:text-accent'
                      : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <span className="material-icons-outlined text-base">apartment</span>
                  Logement
                </Link>
                  </div>
                )}
              </div>

              <div className="pt-2 pb-1 px-3 text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                Configuration
              </div>
              <Link
                to={`/admin/projects/${currentProjectId}/configuration/attribute-data`}
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(`/admin/projects/${currentProjectId}/configuration/attribute-data`)
                    ? 'bg-primary/10 text-primary dark:text-accent'
                    : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                }`}
              >
                <span className="material-icons-outlined text-lg">data_object</span>
                Attribute data
              </Link>
              <Link
                to={`/admin/projects/${currentProjectId}/configuration/attribute-data/parties-communes`}
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(`/admin/projects/${currentProjectId}/configuration/attribute-data/parties-communes`)
                    ? 'bg-primary/10 text-primary dark:text-accent'
                    : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                }`}
              >
                <span className="material-icons-outlined text-lg">meeting_room</span>
                Partie commune
              </Link>
            </div>
          )}

          <div className="pt-4 pb-2 px-3 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
            Administration
          </div>
          <Link
            to="/admin/users"
            className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              isActive('/admin/users')
                ? 'bg-primary/10 text-primary dark:text-accent'
                : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
            }`}
          >
            <span className="material-icons-outlined text-lg">people</span>
            Utilisateurs
          </Link>

          {isSuperRoot && (
            <>
              <div className="pt-4 pb-2 px-3 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                Super utilisateur
              </div>
              <Link
                to="/admin/influxdb-test"
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive('/admin/influxdb-test')
                    ? 'bg-primary/10 text-primary dark:text-accent'
                    : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                }`}
              >
                <span className="material-icons-outlined text-lg">api</span>
                InfluxDB API Test
              </Link>
              <Link
                to="/admin/metric-catalog"
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive('/admin/metric-catalog')
                    ? 'bg-primary/10 text-primary dark:text-accent'
                    : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                }`}
              >
                <span className="material-icons-outlined text-lg">category</span>
                Metric Catalog
              </Link>
              <Link
                to="/admin/smalt-core-ingest-sync"
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive('/admin/smalt-core-ingest-sync')
                    ? 'bg-primary/10 text-primary dark:text-accent'
                    : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                }`}
              >
                <span className="material-icons-outlined text-lg">sync</span>
                Smalt Core Ingest Sync
              </Link>
              <Link
                to="/admin/super-topology-debug"
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive('/admin/super-topology-debug')
                    ? 'bg-primary/10 text-primary dark:text-accent'
                    : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                }`}
              >
                <span className="material-icons-outlined text-lg">bug_report</span>
                Debug Topology
              </Link>
              <Link
                to="/admin/super-zipato-analysis"
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive('/admin/super-zipato-analysis')
                    ? 'bg-primary/10 text-primary dark:text-accent'
                    : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                }`}
              >
                <span className="material-icons-outlined text-lg">sync_alt</span>
                Zipato Analysis
              </Link>
              <Link
                to="/admin/super-check-device-exist"
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive('/admin/super-check-device-exist')
                    ? 'bg-primary/10 text-primary dark:text-accent'
                    : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                }`}
              >
                <span className="material-icons-outlined text-lg">fact_check</span>
                Check devices Zipato
              </Link>
              <Link
                to="/admin/super-associations"
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive('/admin/super-associations')
                    ? 'bg-primary/10 text-primary dark:text-accent'
                    : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                }`}
              >
                <span className="material-icons-outlined text-lg">link</span>
                Super Associations
              </Link>
              <Link
                to="/admin/super-import-equipments"
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive('/admin/super-import-equipments')
                    ? 'bg-primary/10 text-primary dark:text-accent'
                    : 'text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800'
                }`}
              >
                <span className="material-icons-outlined text-lg">upload</span>
                Import Équipements
              </Link>
            </>
          )}
        </nav>
      </div>
      <div className="p-4 border-t border-slate-200 dark:border-[#1E293B]">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-gradient-to-tr from-primary to-purple-500 flex items-center justify-center text-white text-xs font-bold">
            {user?.fullName ? getUserInitials(user.fullName) : 'AD'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
              {user?.fullName || 'Admin Root'}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
              {user?.email || 'admin@gtb-platform.com'}
            </p>
          </div>
        </div>
        <button
          onClick={logout}
          className="mt-3 w-full px-3 py-2 text-sm font-medium text-slate-600 dark:text-[#94A3B8] hover:bg-slate-100 dark:hover:bg-gray-800 rounded-md transition-colors flex items-center gap-2"
        >
          <span className="material-icons-outlined text-lg">logout</span>
          Déconnexion
        </button>
      </div>
      </aside>
    </>
  );
}
